import { useEffect, useRef, useState } from 'react';
import { api, ENDPOINTS } from '../lib/api.js';
import { CONTACT, WHATSAPP } from '../content/site.js';
import Button from './ui/Button.jsx';

const BIRD_BANDS = [
  'Under 1,000',
  '1,000 – 5,000',
  '5,000 – 20,000',
  '20,000 – 50,000',
  'Over 50,000',
];

const METHODS = [
  'Paper records / notebooks',
  'Excel or Google Sheets',
  'WhatsApp messages from managers',
  'Another software system',
  'Nothing formal yet',
];

const COUNTRIES = ['Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'Other'];

const EMPTY = {
  name: '',
  farm_name: '',
  email: '',
  phone: '',
  country: 'Kenya',
  bird_count_band: '',
  current_method: '',
  message: '',
};

export default function DemoRequestForm({ onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);
  const panelRef = useRef(null);

  /*
   * On a phone the form is taller than the screen, so submitting from the
   * bottom of it left the confirmation above the fold and the page apparently
   * unchanged — the visitor had to scroll up to find out whether it worked.
   *
   * onSuccess runs first because the page drops its sidebar and recentres the
   * card, which moves the target; the scroll waits a frame for that to land.
   * Focus goes to the panel as well, so a keyboard or screen-reader user is
   * moved to the result rather than left where the submit button used to be.
   */
  useEffect(() => {
    if (status !== 'done') return;

    onSuccess?.();

    const raf = window.requestAnimationFrame(() => {
      const el = panelRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(raf);
  }, [status, onSuccess]);

  // Spam defences that need no third-party captcha:
  //  - honeypot: a field hidden from humans; bots fill everything
  //  - time trap: a genuine person cannot complete this in under ~3 seconds
  const honeypot = useRef('');
  const openedAt = useRef(Date.now());

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please tell us your name.';
    if (!form.phone.trim() && !form.email.trim())
      e.phone = 'We need a phone number or an email so we can reach you.';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'That email address looks incomplete.';
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError(null);

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      // Move focus to the first problem so keyboard and screen-reader users
      // are not left guessing why nothing happened.
      document.querySelector(`[name="${Object.keys(found)[0]}"]`)?.focus();
      return;
    }

    setStatus('sending');
    try {
      await api.post(ENDPOINTS.demoRequest, {
        ...form,
        source: 'website',
        // Server-side spam signals
        _hp: honeypot.current,
        _elapsed: Math.round((Date.now() - openedAt.current) / 1000),
      });
      setStatus('done');
    } catch (err) {
      const res = err?.response;
      if (res?.status === 422 && res.data?.errors) {
        setErrors(
          Object.fromEntries(Object.entries(res.data.errors).map(([k, v]) => [k, v[0]]))
        );
        setStatus('idle');
        return;
      }
      if (res?.status === 429) {
        setServerError('Too many requests just now. Please try again in a minute.');
      } else {
        setServerError(
          'We could not send that just now. Please reach us on WhatsApp instead. It goes straight to the team.'
        );
      }
      setStatus('error');
    }
  };

  if (status === 'done') {
    return <SuccessPanel form={form} panelRef={panelRef} />;
  }

  return (
    <form onSubmit={onSubmit} noValidate className="rounded-2xl border border-line bg-white p-6 sm:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Your name" name="name" required error={errors.name}>
          <input
            {...inputProps('name')}
            value={form.name}
            onChange={set('name')}
            autoComplete="name"
          />
        </Field>

        <Field label="Farm name" name="farm_name" error={errors.farm_name}>
          <input
            {...inputProps('farm_name')}
            value={form.farm_name}
            onChange={set('farm_name')}
            autoComplete="organization"
          />
        </Field>

        <Field
          label="Phone / WhatsApp"
          name="phone"
          hint="How we usually reach farms"
          error={errors.phone}
        >
          <input
            {...inputProps('phone')}
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={set('phone')}
            placeholder="+254 7…"
            autoComplete="tel"
          />
        </Field>

        <Field label="Email" name="email" error={errors.email}>
          <input
            {...inputProps('email')}
            type="email"
            inputMode="email"
            value={form.email}
            onChange={set('email')}
            autoComplete="email"
          />
        </Field>

        <Field label="Country" name="country">
          <select {...inputProps('country')} value={form.country} onChange={set('country')}>
            {COUNTRIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>

        <Field label="How many birds?" name="bird_count_band">
          <select
            {...inputProps('bird_count_band')}
            value={form.bird_count_band}
            onChange={set('bird_count_band')}
          >
            <option value="">Select…</option>
            {BIRD_BANDS.map((b) => (
              <option key={b}>{b}</option>
            ))}
          </select>
        </Field>

        <div className="sm:col-span-2">
          <Field label="How do you keep records today?" name="current_method">
            <select
              {...inputProps('current_method')}
              value={form.current_method}
              onChange={set('current_method')}
            >
              <option value="">Select…</option>
              {METHODS.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label="Anything specific you want to see?" name="message">
            <textarea
              {...inputProps('message')}
              rows={4}
              value={form.message}
              onChange={set('message')}
            />
          </Field>
        </div>
      </div>

      {/* Honeypot. Hidden from people, not from bots. aria-hidden + tabIndex
          keep it away from screen readers and keyboard navigation. */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="company_website">Company website</label>
        <input
          id="company_website"
          name="company_website"
          tabIndex={-1}
          autoComplete="off"
          onChange={(e) => (honeypot.current = e.target.value)}
        />
      </div>

      {serverError && (
        <p role="alert" className="mt-5 rounded-lg bg-alert/10 px-4 py-3 text-[13.5px] text-alert">
          {serverError}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Button variant="primary" type="submit" disabled={status === 'sending'} className="min-w-[180px] disabled:opacity-60">
          {status === 'sending' ? 'Sending…' : 'Request a demo'}
        </Button>
        <p className="text-[12.5px] text-muted">
          Or message us on{' '}
          <a href={WHATSAPP.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-field underline">
            WhatsApp
          </a>
        </p>
      </div>
    </form>
  );
}

function inputProps(name) {
  return {
    id: name,
    name,
    className:
      'w-full rounded-lg border border-line bg-white px-3.5 py-2.5 text-[14px] text-deep placeholder:text-muted/60 focus:border-field',
  };
}

function Field({ label, name, hint, required, error, children }) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block text-[13px] font-semibold text-deep">
        {label}
        {required && <span className="text-alert"> *</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-[12px] text-muted">{hint}</p>}
      {error && (
        <p role="alert" className="mt-1 text-[12px] font-medium text-alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * There is no email in this phase, so this panel IS the receipt — it restates
 * what was submitted and gives a direct channel, so a lead the team never
 * follows up on is still recoverable from the farmer's side.
 */
function SuccessPanel({ form, panelRef }) {
  return (
    <div
      ref={panelRef}
      tabIndex={-1}
      role="status"
      aria-live="polite"
      className="rounded-2xl border border-field/30 bg-wash p-7 outline-none sm:p-9"
    >
      <p className="eyebrow mb-3">Request received</p>
      <h2 className="mb-3 font-display text-[22px] font-bold text-deep">
        Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''}. We&rsquo;ll be in touch.
      </h2>
      <p className="mb-6 max-w-[520px] text-[14.5px] text-muted">
        We usually reply within one working day, on WhatsApp unless you&rsquo;ve asked otherwise. If
        it&rsquo;s urgent, message us directly. It reaches the same team.
      </p>

      <dl className="mb-7 grid gap-2 text-[13.5px] sm:grid-cols-2">
        {[
          ['Name', form.name],
          ['Farm', form.farm_name],
          ['Phone', form.phone],
          ['Email', form.email],
          ['Country', form.country],
          ['Flock size', form.bird_count_band],
        ]
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <dt className="text-muted">{k}:</dt>
              <dd className="font-semibold text-deep">{v}</dd>
            </div>
          ))}
      </dl>

      <div className="flex flex-wrap gap-3.5">
        <Button href={WHATSAPP.url} variant="dark">
          Message us on WhatsApp
        </Button>
        <Button href={`tel:+${CONTACT.phones[0].e164}`} variant="white" className="border border-line">
          Call {CONTACT.phones[0].display}
        </Button>
      </div>
    </div>
  );
}
